import React, { FC, ReactNode, useState } from 'react';

import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import dynamic from 'next/dynamic';
import { ButtonWithIcon } from 'src/v2/components/ui/buttons/buttonWithIcon';
import { CollapsableContainer } from 'src/v2/components/ui/containers/collapse';
import { NativeTokenOrbital, PerspectiveGridFull, Wormhole } from 'src/v2/components/ui/images';

import { useAllVaultData } from '~components/hooks/protocol/vaults/useAllVaultData';
import { NormalDivider } from '~components/shared/divider';
import { primaryColors, vestaAltGradient, vestaGradient, white } from '~components/theme/styles/colors';
import { secondaryBox } from '~components/theme/styles/mixins';
import { getGradientTypographySx } from '~components/theme/typography';
import { EDisplayType } from '~components/types';
import { displayFormatUnits } from '~components/utils/utils';
import { ENativeAsset } from '~config/types';

import SlideWrapper from '../SlideWrapper';

const MobileFadingContainer = dynamic(
  () =>
    import('src/v2/components/ui/containers/mobile').then(
      ({ MobileFadingContainer }) => MobileFadingContainer,
    ),
  { ssr: false },
);

const CollapseSectionMobile = () => {
  const [sectionView, setSectionView] = useState<1 | 2 | 3>(1);
  const {
    computed: { totalCollateralUsd, totalSystemDebtVst },
  } = useAllVaultData();
  function collapse(section: 1 | 2 | 3) {
    if (sectionView === section) setSectionView(1);
    else setSectionView(section);
  }

  return (
    <Grid item container spacing={4} xs={12}>
      <Grid item xs={12} textAlign={'center'} mb={8}>
        <Wormhole />
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={2}>
          <CollapsableContainer
            collapsed={sectionView !== 1}
            title="Secure and reliable by default"
            onCollapse={() => collapse(1)}
          >
            <Stack spacing={4} py={4}>
              <Typography variant={'body1'}>
                {displayFormatUnits({ value: totalSystemDebtVst, displayType: EDisplayType.Token })} of minted
                VST is overcollateralized by{' '}
                {displayFormatUnits({ value: totalCollateralUsd, displayType: EDisplayType.USD })} of
                collateral deposited in our vaults.
              </Typography>
              <Typography variant={'body2'}>All collaterals are rated and constantly monitored.</Typography>
            </Stack>
          </CollapsableContainer>
          <NormalDivider />
          <CollapsableContainer
            collapsed={sectionView !== 2}
            title="Unbiased and Uncensorable"
            onCollapse={() => collapse(2)}
          >
            <Stack spacing={4} py={4}>
              <Typography variant={'body1'}>
                VST can be freely borrowed, traded, and invested on a wide panel of platforms and apps, with
                no blacklisting or censorship.
              </Typography>
              <Stack direction={'row'} spacing={4}>
                <ButtonWithIcon sx={{ width: 230 }} iconType="asterisk">
                  BUY VST ON CURVE
                </ButtonWithIcon>
                <ButtonWithIcon sx={{ width: 230 }} iconType="asterisk">
                  OUR PARTNERS
                </ButtonWithIcon>
              </Stack>
            </Stack>
          </CollapsableContainer>
          <NormalDivider />
          <CollapsableContainer
            collapsed={sectionView !== 3}
            title="Innovation-Driven"
            onCollapse={() => collapse(3)}
          >
            <Stack spacing={4} py={4}>
              <Typography variant={'body1'}>
                Protected by the Vesta Reference Rate, secured by the Stability Pools and safeguarded by the
                Saving Module.
              </Typography>
              <ButtonWithIcon sx={{ width: 300 }} iconType="asterisk">
                LEARN MORE ABOUT THE FEATURES
              </ButtonWithIcon>
            </Stack>
          </CollapsableContainer>
        </Stack>
      </Grid>
    </Grid>
  );
};

const SimplePanel: FC<{ img: ReactNode; title: string; children: ReactNode }> = ({
  img,
  title,
  children,
}) => {
  return (
    <Box sx={{ ...secondaryBox, height: '100%' }}>
      <Stack spacing={3}>
        <Box width={120} height={120}>
          {img}
        </Box>
        <Typography
          variant="h6"
          sx={{ background: vestaAltGradient, backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
        >
          {title}
        </Typography>
        {children}
      </Stack>
    </Box>
  );
};

const PanelSectionApp = () => {
  const allVaults = useAllVaultData();

  return (
    <Grid container spacing={4}>
      <Grid item xs={4}>
        <SimplePanel img={<Wormhole />} title={'Modern & Innovation-Driven'}>
          {' '}
          <Typography variant="body2" color={primaryColors[100]}>
            Protected by the Vesta Reference Rate, secured by the Stability Pools and safeguarded by the
            Saving Module.
          </Typography>{' '}
        </SimplePanel>
      </Grid>
      <Grid item xs={4}>
        <SimplePanel img={<Wormhole />} title={'Secure and reliable by default'}>
          <Typography variant="body2" color={primaryColors[100]}>
            <Typography component={'span'} variant="body2" sx={getGradientTypographySx(vestaGradient)}>
              {displayFormatUnits({
                value: allVaults.computed.totalSystemDebtVst,
                displayType: EDisplayType.Token,
              })}
            </Typography>{' '}
            of minted VST is overcollateralized by{' '}
            <Typography component={'span'} variant="body2" sx={getGradientTypographySx(vestaGradient)}>
              {displayFormatUnits({
                value: allVaults.computed.totalCollateralUsd,
                displayType: EDisplayType.USD,
              })}
            </Typography>{' '}
            of collateral deposited in our vaults. All collaterals are rated and constantly monitored.
          </Typography>{' '}
        </SimplePanel>
      </Grid>
      <Grid item xs={4}>
        <SimplePanel img={<Wormhole />} title={'Unbiased and Uncensorable'}>
          {' '}
          <Typography variant="body2" color={primaryColors[100]}>
            VST can be freely borrowed, traded, and invested on a wide panel of platforms and apps, with no
            blacklisting or censorship.
          </Typography>{' '}
        </SimplePanel>
      </Grid>
    </Grid>
  );
};

const StableCoinSlide: FC = () => {
  return (
    <SlideWrapper>
      <Grid container spacing={12}>
        <Grid position={'relative'} item xs={12}>
          <Box position={'absolute'} width={'100%'} height={'40%'} bottom={-30} right={-30}>
            <PerspectiveGridFull />
          </Box>
          <Box display={'flex'} justifyContent={'center'}>
            <NativeTokenOrbital asset={ENativeAsset.VST} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={2} justifyContent={'center'} textAlign={'center'}>
            <Typography variant={'h4'} color={primaryColors[400]}>
              The Gateway to
            </Typography>
            <Typography variant={'h2'} color={primaryColors[100]}>
              Vesta Stablecoin
            </Typography>
            <Typography variant={'body1'} color={primaryColors[400]}>
              Experience Unparalleled Security & Censorship-Resistance with our overcollateralized stablecoin:
              VST.
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={18} mt={6}>
        <Grid item xs={12}>
          <MobileFadingContainer app={<PanelSectionApp />} mobile={<CollapseSectionMobile />} />
        </Grid>
        <Grid item xs={12} textAlign={'center'}>
          <Button variant={'secondary'} sx={{ width: 310 }}>
            LEARN MORE ABOUT VST FEATURES
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent={'space-between'} spacing={4}>
            <Typography
              sx={{
                background: vestaGradient,
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
              variant={'h5'}
            >
              Freely borrow and use VST anywhere, anytime
            </Typography>

            <Box
              sx={{
                borderLeft: { xs: undefined, sm: `1px ${white['15%']} solid` },
                borderBottom: { xs: `1px ${white['15%']} solid`, sm: undefined },
              }}
            />

            <Typography
              sx={{
                textAlign: { xs: 'left', sm: 'right' },
                background: vestaGradient,
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
              variant={'h5'}
            >
              No blacklisting, transferrable all across Arbitrum
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </SlideWrapper>
  );
};

export default StableCoinSlide;
